import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reportApi } from '../lib/api';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

interface ReviewSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string | null;
  reviews: any[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  onApprove: (reportId: string) => void;
  onReject: (reportId: string, notes: string) => void;
}

export default function ReviewSlideOver({
  isOpen,
  onClose,
  reportId,
  reviews,
  currentIndex,
  onNavigate,
  onApprove,
  onReject,
}: ReviewSlideOverProps) {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [showFullText, setShowFullText] = useState(false);

  // Reset state when navigating to a different report
  useEffect(() => {
    setRejectMode(false);
    setRejectNotes('');
    setShowFullText(false);
  }, [reportId]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        onNavigate(currentIndex - 1);
      }
      if (e.key === 'ArrowRight' && currentIndex < reviews.length - 1) {
        onNavigate(currentIndex + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, reviews.length, onNavigate]);

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', reportId],
    queryFn: async () => {
      const response = await reportApi.getReport(reportId!);
      return response.data;
    },
    enabled: !!reportId && isOpen,
  });

  const violations = report?.violations || [];
  const currentReview = reviews[currentIndex];

  const handleApprove = () => {
    if (reportId) onApprove(reportId);
  };

  const handleReject = () => {
    if (reportId && rejectNotes.trim()) {
      onReject(reportId, rejectNotes);
    }
  };

  const severityCounts = violations.reduce(
    (acc: Record<string, number>, v: any) => {
      acc[v.severity] = (acc[v.severity] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        {/* Panel — slides from right */}
        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="relative w-full max-w-xl bg-white shadow-xl flex flex-col h-full">
              {/* ─── Header ─── */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="min-w-0 flex-1">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 truncate">
                    {currentReview?.report?.upload?.fileName || 'Report'}
                  </Dialog.Title>
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">
                    {currentReview?.report?.upload?.fileType || 'FILE'}
                  </span>
                </div>

                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  {/* Prev/Next */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onNavigate(currentIndex - 1)}
                      disabled={currentIndex <= 0}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                    </button>
                    <span className="text-xs text-gray-500 tabular-nums">
                      {currentIndex + 1} of {reviews.length}
                    </span>
                    <button
                      onClick={() => onNavigate(currentIndex + 1)}
                      disabled={currentIndex >= reviews.length - 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>

                  {/* Close */}
                  <button
                    onClick={onClose}
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* ─── Scrollable Content ─── */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                {isLoading ? (
                  <LoadingSkeleton />
                ) : report ? (
                  <>
                    {/* Status Banner */}
                    {violations.length > 0 ? (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-yellow-900">
                            {violations.length} Issue{violations.length !== 1 ? 's' : ''} Detected
                          </p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {severityCounts.CRITICAL > 0 && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-red-200 text-red-800">
                                {severityCounts.CRITICAL} Critical
                              </span>
                            )}
                            {severityCounts.HIGH > 0 && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-orange-200 text-orange-800">
                                {severityCounts.HIGH} High
                              </span>
                            )}
                            {severityCounts.MEDIUM > 0 && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-yellow-200 text-yellow-800">
                                {severityCounts.MEDIUM} Medium
                              </span>
                            )}
                            {severityCounts.LOW > 0 && (
                              <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-blue-200 text-blue-800">
                                {severityCounts.LOW} Low
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
                        <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                        <p className="font-semibold text-green-900">No Violations Detected</p>
                      </div>
                    )}

                    {/* Violations */}
                    {violations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Issues</h4>
                        <div className="space-y-3">
                          {violations.map((violation: any) => (
                            <div
                              key={violation.ruleId || violation.name}
                              className={`border-l-4 pl-4 py-3 rounded-r ${
                                violation.severity === 'CRITICAL' ? 'border-red-500 bg-red-50' :
                                violation.severity === 'HIGH' ? 'border-orange-500 bg-orange-50' :
                                violation.severity === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' :
                                'border-blue-500 bg-blue-50'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <p className="font-semibold text-gray-900 text-sm">
                                  {violation.ruleId}: {violation.ruleName}
                                </p>
                                <span className={`ml-3 px-2 py-0.5 text-xs font-semibold rounded flex-shrink-0 ${
                                  violation.severity === 'CRITICAL' ? 'bg-red-200 text-red-800' :
                                  violation.severity === 'HIGH' ? 'bg-orange-200 text-orange-800' :
                                  violation.severity === 'MEDIUM' ? 'bg-yellow-200 text-yellow-800' :
                                  'bg-blue-200 text-blue-800'
                                }`}>
                                  {violation.severity}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{violation.description || violation.category}</p>
                              {violation.detectedText && (
                                <div className="mt-2 p-2 bg-white border border-gray-200 rounded">
                                  <p className="text-xs text-gray-600 font-medium">Detected Text:</p>
                                  <p className="text-sm text-gray-800 mt-0.5">{violation.detectedText}</p>
                                </div>
                              )}
                              {violation.requiredAction && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                  <p className="text-xs text-blue-800 font-medium">Required Action:</p>
                                  <p className="text-sm text-blue-900 mt-0.5">{violation.requiredAction}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* UDAAP Risk Scores */}
                    {report.udaapUnfairScore !== null && report.udaapUnfairScore !== undefined && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">UDAAP Risk Assessment</h4>
                        <div className="grid grid-cols-3 gap-3">
                          <UdaapScoreCard label="Unfair" score={report.udaapUnfairScore || 0} />
                          <UdaapScoreCard label="Deceptive" score={report.udaapDeceptiveScore || 0} />
                          <UdaapScoreCard label="Abusive" score={report.udaapAbusiveScore || 0} />
                        </div>
                      </div>
                    )}

                    {/* Extracted Text */}
                    {report.extractedText && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Extracted Text</h4>
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border border-gray-200 max-h-60 overflow-y-auto">
                          {showFullText
                            ? report.extractedText
                            : report.extractedText.slice(0, 500)}
                          {!showFullText && report.extractedText.length > 500 && '...'}
                        </pre>
                        {report.extractedText.length > 500 && (
                          <button
                            onClick={() => setShowFullText(!showFullText)}
                            className="text-xs text-ocean-600 hover:text-ocean-700 font-medium mt-1"
                          >
                            {showFullText ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              {/* ─── Sticky Footer ─── */}
              <div className="border-t border-gray-200 px-6 py-4 bg-white flex-shrink-0">
                {rejectMode ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-2">Rejection Notes (required)</p>
                    <textarea
                      value={rejectNotes}
                      onChange={(e) => setRejectNotes(e.target.value)}
                      placeholder="Explain why this report is being rejected..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent mb-3"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleReject}
                        disabled={!rejectNotes.trim()}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XCircleIcon className="h-4 w-4 mr-1.5" />
                        Confirm Rejection
                      </button>
                      <button
                        onClick={() => { setRejectMode(false); setRejectNotes(''); }}
                        className="flex-1 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-50 text-sm font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleApprove}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                      Approve
                    </button>
                    <button
                      onClick={() => setRejectMode(true)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1.5" />
                      Reject
                    </button>
                    <Link
                      to={`/reports/${reportId}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-ocean-600 hover:text-ocean-700 whitespace-nowrap"
                    >
                      Full Report
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function UdaapScoreCard({ label, score }: { label: string; score: number }) {
  const bg = score <= 30 ? 'bg-green-50' : score <= 60 ? 'bg-yellow-50' : 'bg-red-50';
  const text = score <= 30 ? 'text-green-600' : score <= 60 ? 'text-yellow-600' : 'text-red-600';
  const risk = score <= 30 ? 'Low' : score <= 60 ? 'Medium' : 'High';

  return (
    <div className={`rounded-lg p-3 ${bg}`}>
      <div className="text-xs text-gray-600">{label}</div>
      <div className={`text-lg font-bold ${text}`}>{risk} Risk</div>
      <div className="text-xs text-gray-500">{score}/100</div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-16 bg-gray-100 rounded-lg" />
      <div className="space-y-3">
        <div className="h-4 bg-gray-100 rounded w-24" />
        <div className="h-24 bg-gray-100 rounded-lg" />
        <div className="h-24 bg-gray-100 rounded-lg" />
      </div>
      <div className="space-y-3">
        <div className="h-4 bg-gray-100 rounded w-32" />
        <div className="grid grid-cols-3 gap-3">
          <div className="h-20 bg-gray-100 rounded-lg" />
          <div className="h-20 bg-gray-100 rounded-lg" />
          <div className="h-20 bg-gray-100 rounded-lg" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-100 rounded w-28" />
        <div className="h-32 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );
}
